import React, { useState, useEffect, useCallback, useRef } from "react";
import { Layout, Button, notification, DatePicker, Space, message, Table, Tag, Card, Select } from "antd";
import jsPDF from 'jspdf';
import { Row, Form, Col } from "antd";
import { DownloadOutlined, FilterOutlined, ClearOutlined, SearchOutlined } from "@ant-design/icons";
import * as XLSX from 'xlsx';
import autoTable from 'jspdf-autotable';

import TopHeader from "../common/TopHeader/TopHeader";
import Sidebar from "../common/Sidebar/Sidebar";
// import FooterDashboard from "../common/FooterDashboard/FooterDashboard";
import dayjs from "dayjs";
import ReportsApi from "../common/Apis/ReportsApi";
import Pagination from "../common/Pagination/Pagination";
import "../common/Pagination/Pagination.css";

const HotHoldReport = () => {
    const { Content } = Layout;
    const [formAdd] = Form.useForm();
    const [hotHoldData, setHotHoldData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    
    // Filter states - Selected values
    const [batchCodeFilter, setBatchCodeFilter] = useState(null);
    const [storageLocationFilter, setStorageLocationFilter] = useState(null);
    const [recordedByFilter, setRecordedByFilter] = useState(null);
    
    // Filter options
    const [batchOptions, setBatchOptions] = useState([]);
    const [storageLocationOptions, setStorageLocationOptions] = useState([]);
    const [recordedByOptions, setRecordedByOptions] = useState([]);
    
    // Pagination state using your custom pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Check if any filter is applied
    const hasActiveFilters = batchCodeFilter || storageLocationFilter || recordedByFilter;

    // Display data - only show filteredData if filters are applied
    const displayData = hasActiveFilters ? filteredData : hotHoldData;

    const PageLoading = useCallback(() => {
        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
        }, 800);
    }, []);

    // Extract unique filter options from data
    const extractFilterOptions = useCallback((data) => {
        const batches = new Set();
        const locations = new Set();
        const recordedBy = new Set();

        data.forEach((item) => {
            if (item?.productionDTO?.batchCode) {
                batches.add(item.productionDTO.batchCode);
            }
            if (item?.storageDTO?.name) {
                locations.add(item.storageDTO.name);
            }
            if (item?.createdBy) {
                recordedBy.add(item.createdBy);
            }
        });

        setBatchOptions(Array.from(batches).sort());
        setStorageLocationOptions(Array.from(locations).sort());
        setRecordedByOptions(Array.from(recordedBy).sort());
    }, []);

    // Apply filters
    const applyFilters = useCallback((data) => {
        if (!data || data.length === 0) {
            setFilteredData([]);
            setTotalItems(0);
            setTotalPages(0);
            return;
        }

        let filtered = [...data];

        // Batch Code filter
        if (batchCodeFilter) {
        filtered = filtered.filter(
            item =>
                item?.productionDTO?.batchCode &&
                item.productionDTO.batchCode.toLowerCase() === batchCodeFilter.toLowerCase()
        );
    }

    if (storageLocationFilter) {
        filtered = filtered.filter(
            item =>
                item?.storageDTO?.name &&
                item.storageDTO.name.toLowerCase() === storageLocationFilter.toLowerCase()
        );
    }

    if (recordedByFilter) {
        filtered = filtered.filter(
            item =>
                item?.createdBy &&
                item.createdBy.toLowerCase() === recordedByFilter.toLowerCase()
        );
    }

        setFilteredData(filtered);
        setTotalItems(filtered.length);
        setTotalPages(Math.ceil(filtered.length / pageSize));
        setCurrentPage(1);
    }, [batchCodeFilter, storageLocationFilter, recordedByFilter, pageSize]);

    useEffect(() => {
    applyFilters(hotHoldData);
    }, [
        batchCodeFilter,
        storageLocationFilter,
        recordedByFilter,
        hotHoldData,
        applyFilters,
    ]);

    // Update everything when hotHoldData changes
    useEffect(() => {
        if (hotHoldData.length > 0) {
            extractFilterOptions(hotHoldData);
            applyFilters(hotHoldData);
        } else {
            setFilteredData([]);
            setTotalItems(0);
            setTotalPages(0);
            setBatchOptions([]);
            setStorageLocationOptions([]);
            setRecordedByOptions([]);
        }
    }, [hotHoldData, extractFilterOptions, applyFilters]);


    // Reset filters
    const resetFilters = () => {
        setBatchCodeFilter(null);
        setStorageLocationFilter(null);
        setRecordedByFilter(null);
        setFilteredData([]);
        setTotalItems(hotHoldData.length);
        setTotalPages(Math.ceil(hotHoldData.length / pageSize));
        setCurrentPage(1);
        message.info("All filters have been reset");
    };

    const handleExportToPdf = () => {
        const dataToExport = displayData;
        
        if (!dataToExport || dataToExport.length === 0) {
            message.warning("No data available to export");
            return;
        }

        setExportLoading(true);

        try {
            const excelData = [];

            dataToExport.forEach((item) => {
                let allergensString = '';

                if (item?.productionDTO?.allergens) {
                    const allergenList = Array.isArray(item.productionDTO.allergens)
                        ? item.productionDTO.allergens
                        : item.productionDTO.allergens.split(',');

                    allergensString = allergenList
                        .map(a => a.trim())
                        .join(', ');
                }

                let correctiveAction = item?.correctiveAction || '-';

                if (correctiveAction !== '-' && correctiveAction !== 'N/A') {
                    if (correctiveAction.toLowerCase().includes('rework')) {
                        correctiveAction = 'Rework';
                    } else if (correctiveAction.toLowerCase().includes('reject')) {
                        correctiveAction = 'Rejected';
                    } else if (correctiveAction.toLowerCase().includes('approved')) {
                        correctiveAction = 'Approved';
                    }
                } else if (correctiveAction === 'N/A') {
                    correctiveAction = '-';
                }

                excelData.push({
                    Date: formatDate(item.hotHoldDate),
                    'Product Description': item?.productionName || '-',
                    'Batch Code': item?.productionDTO?.batchCode || '-',
                    'Took time': item?.createdDate ? dayjs(item.createdDate).format('HH:mm') : '-',
                    'Temperature (°C)': item?.temp && item.temp !== 'N/A'
                        ? `${item.temp}°C`
                        : (item?.temp || '-'),
                    'Storage Location': item?.storageDTO?.name || '-',
                  
                    'Sell By Date': formatDate(item?.productionDTO?.sellByDate),
                    Allergens: allergensString || '-',
                    Quantity: `${(() => {
                        const value = Number(item?.productionDTO?.quantity || 0);
                        const normalStr = value.toLocaleString("fullwide", {
                            useGrouping: false,
                            maximumFractionDigits: 20,
                        });
                        const match = normalStr.match(/^-?\d+(?:\.\d{0,2})?/);
                        return match ? match[0] : "0";
                    })()} ${item?.productionDTO?.unit || ''}`,
                    'Corrective Action': correctiveAction,
                    'Recorded By': item?.createdBy || '-'
                });
            });

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });

            const startDate = formAdd.getFieldValue('startDate');
            const endDate = formAdd.getFieldValue('endDate');

            doc.setFontSize(16);
            doc.text('Hot Hold Report', 14, 15);

            let filterInfo = '';
            if (startDate && endDate) {
                filterInfo += `Date Range: ${dayjs(startDate).format('DD/MM/YYYY')} - ${dayjs(endDate).format('DD/MM/YYYY')}`;
            }
            if (batchCodeFilter) {
                filterInfo += ` | Batch: ${batchCodeFilter}`;
            }
            if (storageLocationFilter) {
                filterInfo += ` | Storage: ${storageLocationFilter}`;
            }
            if (recordedByFilter) {
                filterInfo += ` | Recorded By: ${recordedByFilter}`;
            }
            filterInfo += ` | Total Records: ${dataToExport.length}`;

            doc.setFontSize(10);
            doc.text(filterInfo, 14, 22);

            autoTable(doc, {
                startY: 28,
                head: [Object.keys(excelData[0])],
                body: excelData.map(row => Object.values(row)),
                styles: {
                    fontSize: 7,
                    cellPadding: 2
                },
                headStyles: {
                    fillColor: [79, 129, 189],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold'
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245]
                },
                margin: {
                    left: 5,
                    right: 5
                },
                 columnStyles: {
                0: { cellWidth: 16 }, 
                1: { cellWidth: 20 }, 
                2: { cellWidth: 30 }, 
                3: { cellWidth: 30 }, 
                4: { cellWidth: 30 }, 
                5: { cellWidth: 25 }, 
                6: { cellWidth: 22 }, 
                7: { cellWidth: 22 }, 
                8: { cellWidth: 25 },
                9: { cellWidth: 22 }, 
                10: { cellWidth: 16 }, 
              
            }
            });

            let fileName = `Hot_Hold_Report_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pdf`;

            if (startDate && endDate) {
                const startStr = dayjs(startDate).format('YYYY-MM-DD');
                const endStr = dayjs(endDate).format('YYYY-MM-DD');
                const batchStr = batchCodeFilter ? `_${batchCodeFilter}` : '';
                const storageStr = storageLocationFilter ? `_${storageLocationFilter}` : '';
                const recordedStr = recordedByFilter ? `_${recordedByFilter}` : '';
                fileName = `Hot_Hold_Report_${startStr}_to_${endStr}${batchStr}${storageStr}${recordedStr}.pdf`;
            }

            doc.save(fileName);
            message.success("PDF exported successfully!");

        } catch (error) {
            console.error(error);
            message.error("Failed to export PDF");
        } finally {
            setExportLoading(false);
        }
    };

    const handlePrintButtonClick2 = () => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4'
        });

        const fontSize = 12;
        const margin = 5;
        const cellWidthFactor = 0.295;
        const cellHeightFactor = 0.2;

        doc.setFontSize(fontSize);

        const table = document.querySelector('.table');
        const rows = table.querySelectorAll('tr');

        const pageTitle = 'Hot Hold Report';
        const pageTitleWidth = doc.getStringUnitWidth(pageTitle) * fontSize / doc.internal.scaleFactor;
        const pageTitleXPos = (doc.internal.pageSize.width - pageTitleWidth) / 2;
        doc.text(pageTitleXPos, margin, pageTitle);

        doc.setFontSize(fontSize);
        const currentDate = new Date().toLocaleString();
        const dateTimeWidth = doc.getStringUnitWidth(currentDate) * fontSize / doc.internal.scaleFactor;
        const dateTimeXPos = doc.internal.pageSize.width - margin - dateTimeWidth;
        doc.text(dateTimeXPos, margin, currentDate);

        let yPos = margin + fontSize;

        rows.forEach((row, rowIndex) => {
            const cells = row.querySelectorAll('th, td');
            let xPos = margin;

            cells.forEach((cell, cellIndex) => {
                const cellText = cell.textContent.trim();
                const cellWidth = cell.offsetWidth * cellWidthFactor;
                const cellHeight = row.offsetHeight * cellHeightFactor;

                if (yPos + cellHeight > doc.internal.pageSize.height - margin) {
                    doc.addPage();
                    yPos = margin + fontSize;
                    const pageTitle = 'Hot Hold Report';
                    const pageTitleWidth = doc.getStringUnitWidth(pageTitle) * fontSize / doc.internal.scaleFactor;
                    const pageTitleXPos = (doc.internal.pageSize.width - pageTitleWidth) / 2;
                    doc.text(pageTitleXPos, margin, pageTitle);

                    doc.setFontSize(fontSize);
                    const currentDate = new Date().toLocaleString();
                    const dateTimeWidth = doc.getStringUnitWidth(currentDate) * fontSize / doc.internal.scaleFactor;
                    const dateTimeXPos = doc.internal.pageSize.width - margin - dateTimeWidth;
                    doc.text(dateTimeXPos, margin, currentDate);
                }

                const isHeader = cell.tagName.toLowerCase() === 'th';
                const fontWeight = isHeader ? 'bold' : 'normal';

                doc.setFont(undefined, fontWeight);

                const textWidth = doc.getStringUnitWidth(cellText) * fontSize / doc.internal.scaleFactor;
                const cellPadding = 2;
                let xPosAlign = xPos + (cellWidth - textWidth - cellPadding) / 2;

                if (!isHeader) {
                    xPosAlign = xPos;
                }
                doc.rect(xPos, yPos, cellWidth, cellHeight);
                doc.text(xPosAlign + 1, yPos + 5, cellText);

                xPos += cellWidth;
            });

            yPos += row.offsetHeight * cellHeightFactor;
        });

        window.open(doc.output('bloburl'), '_blank');
    };

    const fetchHotHoldData = useCallback(async (page = 1, pageSize = 10) => {
        const startDate = formAdd.getFieldValue('startDate');
        const endDate = formAdd.getFieldValue('endDate');
        
        if (!startDate || !endDate) {
            return;
        }
        
        try {
            PageLoading();
            const startDateStr = dayjs(startDate).format("DD/MM/YYYY");
            const endDateStr = dayjs(endDate).format("DD/MM/YYYY");
            const startTimestamp = convertToUnixTimestamp(startDateStr);
            const endTimestamp = convertToUnixTimestamp(endDateStr) + (24 * 60 * 60 * 1000);
            
            const response = await ReportsApi.HotHoldReport(startTimestamp, endTimestamp);
            
            let dataArray = [];
            let totalElements = 0;
            
            if (response?.payload) {
                dataArray = response.payload;
                totalElements = response.payload.length;
            } else if (response?.payloadDto?.content) {
                dataArray = response.payloadDto.content;
                totalElements = response.payloadDto.totalElements;
            } else if (Array.isArray(response)) {
                dataArray = response;
                totalElements = response.length;
            } else if (response?.content) {
                dataArray = response.content;
                totalElements = response.totalElements;
            }
            
            setHotHoldData(dataArray);
            // Reset filters when new data is loaded
            setBatchCodeFilter(null);
            setStorageLocationFilter(null);
            setRecordedByFilter(null);
            setFilteredData([]);
            setTotalItems(totalElements);
            setTotalPages(Math.ceil(totalElements / pageSize));
            setCurrentPage(page);
            setPageSize(pageSize);
        } catch (err) {
            console.log(err);
            if (err.apiError?.status) {
                notification.open({
                    message: (
                        <span style={{ fontWeight: "bold" }}>{err.apiError.status}</span>
                    ),
                    description: err.apiError.message,
                    closeIcon: false,
                    duration: 3,
                    style: {
                        backgroundColor: "#fff2f0",
                        color: "red",
                    },
                });
            } else {
                notification.error({
                    message: "Error",
                    description: err?.message || "Failed to fetch hot hold report",
                });
            }
        }
    }, [PageLoading]);

    const handleFilter = (data) => {
        setFilterLoading(true);
        fetchHotHoldData(1, pageSize);
        setTimeout(() => {
            setFilterLoading(false);
        }, 800);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    function formatDate(timestamp) {
        if (!timestamp) return '-';
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return '-';
        const options = { day: "2-digit", month: "short", year: "numeric" };
        return date.toLocaleDateString("en-US", options);
    }

    function formatDateTime(timestamp) {
        if (!timestamp) return '-';
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return '-';
        const options = { day: "2-digit", month: "short", year: "numeric", hour: '2-digit', minute: '2-digit' };
        return date.toLocaleDateString("en-US", options);
    }

    function convertToUnixTimestamp(dateString) {
        const parts = dateString.split("/");
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        const unixTimestamp = Date.UTC(year, month, day);
        return unixTimestamp;
    }

    const handleClear = () => {
        setIsLoading(true);
        setHotHoldData([]);
        setFilteredData([]);
        formAdd.resetFields();
        // Reset filters
        setBatchCodeFilter(null);
        setStorageLocationFilter(null);
        setRecordedByFilter(null);
        const tomorrow = dayjs().add(1, 'day');
        const today = dayjs();
        formAdd.setFieldsValue({
            startDate: today,
            endDate: tomorrow,
        });
        setTimeout(() => {
            setIsLoading(false);
            fetchHotHoldData(1, 10);
        }, 800);
    };

    const getAllergensTag = (allergens) => {
        if (!allergens) return '-';
        const allergenList = Array.isArray(allergens) ? allergens : allergens.split(',');
        return (
            <Space size={[0, 4]} wrap>
                {allergenList.map((allergen, index) => (
                    <Tag key={index} color="orange">{allergen.trim()}</Tag>
                ))}
            </Space>
        );
    };

    const getCorrectiveActionTag = (correctiveAction) => {
        if (!correctiveAction || correctiveAction === 'N/A') return '-';
        if (correctiveAction.toLowerCase().includes('rework')) {
            return <Tag color="orange">Rework</Tag>;
        } else if (correctiveAction.toLowerCase().includes('reject')) {
            return <Tag color="red">Rejected</Tag>;
        } else if (correctiveAction.toLowerCase().includes('approved')) {
            return <Tag color="green">Approved</Tag>;
        }
        return <Tag color="blue">{correctiveAction}</Tag>;
    };

    // Get current page data for client-side pagination
    const getCurrentPageData = () => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return displayData.slice(startIndex, endIndex);
    };

    const currentPageData = getCurrentPageData();

    useEffect(() => {
        const tomorrow = dayjs().add(1, 'day');
        const today = dayjs();
        
        formAdd.setFieldsValue({
            startDate: today,
            endDate: tomorrow,
        });
        
        fetchHotHoldData(1, 10);
    }, []);

    const columns = [
        {
            title: 'Date',
            dataIndex: 'hotHoldDate',
            key: 'hotHoldDate',
            width: '10%',
            align: 'center',  
            render: (date) => formatDate(date),
        },
        {
            title: 'Product Description',
            dataIndex: 'productionName',
            key: 'productionName',
            width: '15%',
            align: 'center',
            render: (text) => text || '-',
        },
        {
            title: 'Batch Code',
            dataIndex: ['productionDTO', 'batchCode'],
            key: 'batchCode',
            width: '12%',
            align: 'center',
            render: (code) => {
                if (!code) return '-';
                return (
                    <div style={{
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        whiteSpace: 'nowrap',
                        maxWidth: '200px',
                        padding: '0px 0px 20px',
                        display: 'block',
                    }}>
                        {code}
                    </div>
                );
            },
        },
        {
            title: 'Temperature (°C)',
            dataIndex: 'temp',
            key: 'temp',
            width: '8%',
            align: 'center',
            render: (temp) => temp && temp !== 'N/A' ? `${temp}°C` : (temp || '-'),
        },
         {
            title: 'Took time',
            dataIndex: 'createdDate',
            key: 'createdDate',
            width: '8%',
            align: 'center',
            render: (date) => date ? dayjs(date).format('HH:mm') : '-',
        },
        {
            title: 'Storage Location',
            dataIndex: ['storageDTO', 'name'],
            key: 'storageName',
            width: '12%',
            align: 'center',
            render: (name) => name || '-',
        },
        {
            title: 'Sell By Date',
            dataIndex: ['productionDTO', 'sellByDate'],
            key: 'sellByDate',
            width: '10%',
            align: 'center',
            render: (date) => formatDate(date),
        },
        {
            title: 'Allergens',
            dataIndex: ['productionDTO', 'allergens'],
            key: 'allergens',
            width: '10%',
            align: 'center',
            render: (allergens) => getAllergensTag(allergens),
        },
        {
            title: 'Quantity',
            dataIndex: ['productionDTO', 'quantity'],
            key: 'quantity',
            width: '8%',
            align: 'center',
            align: 'right',
            render: (qty, record) => {
                const value = Number(qty || 0);
                const normalStr = value.toLocaleString("fullwide", {
                    useGrouping: false,
                    maximumFractionDigits: 20,
                });
                const match = normalStr.match(/^-?\d+(?:\.\d{0,2})?/);
                return `${match ? match[0] : "0"} ${record?.productionDTO?.unit || ''}`;
            },
        },
        {
            title: 'Corrective Action',
            dataIndex: 'correctiveAction',
            key: 'correctiveAction',
            width: '10%',
            align: 'center',
            render: (action) => getCorrectiveActionTag(action),
        },
        {
            title: 'Recorded By',
            dataIndex: 'createdBy',
            key: 'createdBy',
            width: '10%',
            align: 'center',
            render: (name) => name || '-',
        },
    ];

    const centeredColumns = columns.map(col => ({ ...col, align: 'center' }));

    return (
        <Layout style={{ minHeight: "100vh" }}>
            <Sidebar />
            <Layout>
                <TopHeader onPrintResponse2={handlePrintButtonClick2} />
                {isLoading && (
                    <div className="spinner-overlay" id="spinnerOverlay">
                        <div className="spinner"></div>
                    </div>
                )}

                <Content
                    className="form-content nav-content"
                    style={{
                        margin: "14px 16px 0",
                        padding: "30px",
                        background: "white",
                    }}
                >
                    <div>
                        {/* Filter Section - Separated Card */}
                        <Card 
                            style={{ 
                                marginBottom: '24px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '10px' }}>
                                <Space>
                                    <FilterOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Date Range Filter</span>
                                </Space>
                            </div>
                            
                            <Form
                                form={formAdd}
                                name="hot_hold_filter_form"
                                layout="vertical"
                                onFinish={handleFilter}
                            >
                                <Row gutter={[16, 0]}>
                                    <Col lg={8} md={12} sm={24} xs={24}>
                                        <Form.Item
                                            name="startDate"
                                            label="Start Date"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please Select Start Date.",
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                format={"DD/MM/YYYY"}
                                                placeholder="Select start date"
                                                style={{ width: '100%', height: '40px' }}
                                                allowClear={false}
                                                suffixIcon={null}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col lg={8} md={12} sm={24} xs={24}>
                                        <Form.Item
                                            name="endDate"
                                            label="End Date"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please Select End Date.",
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                format={"DD/MM/YYYY"}
                                                placeholder="Select end date"
                                                style={{ width: '100%', height: '40px' }}
                                                allowClear={false}
                                                suffixIcon={null}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col lg={8} md={24} sm={24} xs={24}>
                                        <Form.Item label="Actions">
                                            <Space size="middle" wrap>
                                                <Button
                                                    type="primary"
                                                    htmlType="submit"
                                                    loading={filterLoading}
                                                    icon={<FilterOutlined />}
                                                    style={{ height: '40px', minWidth: '100px' }}
                                                >
                                                    Apply Date Filter
                                                </Button>
                                                <Button
                                                    onClick={() => handleClear()}
                                                    icon={<ClearOutlined />}
                                                    style={{ height: '40px', minWidth: '100px' }}
                                                >
                                                    Clear All
                                                </Button>
                                            </Space>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form>
                        </Card>

                        {/* Advanced Filters Section */}
                        <Card 
                            style={{ 
                                marginBottom: '24px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                background: '#fafafa'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '10px' }}>
                                <Space>
                                    <SearchOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Advanced Filters</span>
                                    <span style={{ fontSize: '12px', color: '#999' }}>
                                        (Filter by Batch Code, Storage Location, Recorded By)
                                    </span>
                                </Space>
                            </div>
                            
                            <Row gutter={[16, 16]}>
                                <Col lg={8} md={12} sm={12} xs={24}>
                                    <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 500, color: '#555' }}>
                                        Batch Code
                                    </div>
                                    <Select
                                        placeholder="All Batches"
                                        style={{ width: '100%' }}
                                        value={batchCodeFilter}
                                        onChange={setBatchCodeFilter}
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) =>
                                            option?.children?.toLowerCase().includes(input.toLowerCase())
                                        }
                                    >
                                        {batchOptions.map(batch => (
                                            <Select.Option key={batch} value={batch}>{batch}</Select.Option>
                                        ))}
                                    </Select>
                                </Col>

                                <Col lg={8} md={12} sm={12} xs={24}>
                                    <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 500, color: '#555' }}>
                                        Storage Location
                                    </div>
                                    <Select
                                        placeholder="All Locations"
                                        style={{ width: '100%' }}
                                        value={storageLocationFilter}
                                        onChange={setStorageLocationFilter}
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) =>
                                            option?.children?.toLowerCase().includes(input.toLowerCase())
                                        }
                                    >
                                        {storageLocationOptions.map(location => (
                                            <Select.Option key={location} value={location}>{location}</Select.Option>
                                        ))}
                                    </Select>
                                </Col>

                                <Col lg={8} md={12} sm={12} xs={24}>
                                    <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 500, color: '#555' }}>
                                        Recorded By
                                    </div>
                                    <Select
                                        placeholder="All Recorded By"
                                        style={{ width: '100%' }}
                                        value={recordedByFilter}
                                        onChange={setRecordedByFilter}
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) =>
                                            option?.children?.toLowerCase().includes(input.toLowerCase())
                                        }
                                    >
                                        {recordedByOptions.map(name => (
                                            <Select.Option key={name} value={name}>{name}</Select.Option>
                                        ))}
                                    </Select>
                                </Col>
                            </Row>
                            
                            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                <Space>
                                  
                                    <Button
                                        onClick={resetFilters}
                                        icon={<ClearOutlined />}
                                        style={{ height: '36px' }}
                                        disabled={!hasActiveFilters}
                                    >
                                        Reset Filters
                                    </Button>
                                </Space>
                                <div style={{ padding: '8px 12px', background: '#e6f7ff', borderRadius: '4px', fontSize: '12px', color: '#1890ff' }}>
                                    <strong>Total Records:</strong> {hotHoldData.length} | 
                                    <strong> Displaying:</strong> {displayData.length} records
                                    {hasActiveFilters && (
                                        <span style={{ marginLeft: '8px' }}>
                                            (Filtered by: 
                                           {batchCodeFilter && ` Batch: ${batchCodeFilter}`}
                                            {storageLocationFilter && `, Storage: ${storageLocationFilter}`}
                                            {recordedByFilter && `, Recorded By: ${recordedByFilter}`}
                                            )
                                        </span>
                                    )}
                                    {hasActiveFilters && displayData.length === 0 && (
                                        <span style={{ color: '#ff4d4f', marginLeft: '8px' }}>
                                            ⚠️ No records match the selected filters
                                        </span>
                                    )}
                                </div>
                            </div>
                        </Card>

                        {/* Report Results Section */}
                        <Card 
                            style={{ 
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ 
                                marginBottom: '16px', 
                                borderBottom: '1px solid #f0f0f0', 
                                paddingBottom: '10px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '10px'
                            }}>
                                <Space>
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Hot Hold Report Results</span>
                                    <span style={{ fontSize: '12px', color: '#999' }}>
                                        {displayData.length > 0 && `(${displayData.length} records found)`}
                                        {hasActiveFilters && displayData.length === 0 && 
                                            ` (No matching records)`
                                        }
                                    </span>
                                </Space>
                                <Button
                                    type="primary"
                                    onClick={handleExportToPdf}
                                    loading={exportLoading}
                                    icon={<DownloadOutlined />}
                                    style={{ 
                                        height: '36px',
                                        backgroundColor: '#52c41a',
                                        borderColor: '#52c41a'
                                    }}
                                    disabled={displayData.length === 0}
                                >
                                    Export Hot Hold Report PDF
                                </Button>
                            </div>
                            
                            <div className="dateFilterTable" style={{ overflowX: 'auto' }}>
                                <Table
                                    columns={centeredColumns}
                                    dataSource={currentPageData}
                                    rowKey="id"
                                    pagination={false}
                                    bordered
                                    className="users-table Reportable"
                                    scroll={{ x: 'max-content' }}
                                    locale={{
                                        emptyText: hasActiveFilters ? 'No records match the selected filters' : 'No data available'
                                    }}
                                />
                            </div>
                            
                            {/* Custom Pagination */}
                            {displayData.length > 0 && (
                                <div style={{ marginTop: '20px' }}>
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                        maxVisiblePages={5}
                                        showPageInfo={true}
                                        totalItems={totalItems}
                                        pageSize={pageSize}
                                    />
                                </div>
                            )}
                        </Card>
                    </div>
                </Content>

                {/* <FooterDashboard /> */}
            </Layout>
        </Layout>
    );
};

export default HotHoldReport;